import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit as EditIcon,
  Search,
  Home as HomeIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading,
  setError,
  setDocuments,
  setSelectedDocument,
  setPage,
  setPageSize,
  setSearch,
  clearSelectedDocument,
  addDocument,
  updateDocument as updateDocumentAction,
  deleteDocument as deleteDocumentAction,
} from '../../store/slices/documentsSlice';
import {
  GET_DOCUMENTS_LIST,
  GET_DOCUMENT,
} from '../../graphql/queries/documents.queries';
import { DELETE_DOCUMENT, UPDATE_DOCUMENT } from '../../graphql/mutations/documents.mutations';
import {
  FindDocumentListInput,
  FindDocumentInput,
  DeleteDocumentInput,
  UpdateDocumentInput,
  DocumentsListResponse,
  DocumentResponse,
  DeleteDocumentResponse,
  UpdateDocumentResponse,
  Document,
} from '../../types/documents.types';
import ViewDocumentModal from '../../components/Documents/ViewDocumentModal';
import EditDocumentModal, { EditDocumentFormData } from '../../components/Documents/EditDocumentModal';
import ConfirmDeleteDialog from '../../components/Documents/ConfirmDeleteDialog';
import UploadDocumentModal from '../../components/Documents/UploadDocumentModal';
import MainLayout from '../../components/Layout/MainLayout';
import './Documents.css';

const Documents: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { documents, selectedDocument, loading, error, pagination, search } = useAppSelector(
    (state) => state.documents
  );
  const { user } = useAppSelector((state) => state.auth);

  const [searchInput, setSearchInput] = useState(search);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [fetchDocuments, { loading: fetchingList }] = useLazyQuery<
    { chatbotDocuments: DocumentsListResponse },
    { params: FindDocumentListInput }
  >(GET_DOCUMENTS_LIST, {
    fetchPolicy: 'network-only',
  });

  const [fetchDocument] = useLazyQuery<
    { chatbotDocument: DocumentResponse },
    { params: FindDocumentInput }
  >(GET_DOCUMENT, {
    fetchPolicy: 'network-only',
  });

  const [updateDocumentMutation] = useMutation<
    { updateChatbotDocument: UpdateDocumentResponse },
    { params: UpdateDocumentInput }
  >(UPDATE_DOCUMENT);

  const [deleteDocumentMutation] = useMutation<
    { deleteChatbotDocument: DeleteDocumentResponse },
    { params: DeleteDocumentInput }
  >(DELETE_DOCUMENT);

  useEffect(() => {
    loadDocuments();
  }, [pagination.page, pagination.pageSize, search]);

  const loadDocuments = async () => {
    if (!user?.userId) {
      dispatch(setError('Usuario no autenticado'));
      return;
    }

    try {
      dispatch(setLoading(true));

      const { data } = await fetchDocuments({
        variables: {
          params: {
            userIds: [],
            search: search || null,
            page: pagination.page,
            pageSize: pagination.pageSize,
          },
        },
      });

      if (data?.chatbotDocuments?.success && data.chatbotDocuments.data) {
        dispatch(setDocuments(data.chatbotDocuments.data));
      } else {
        const errorMessage =
          data?.chatbotDocuments?.message || 'Error al cargar documentos';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading documents:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSearch = () => {
    dispatch(setSearch(searchInput));
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    dispatch(setPage(model.page + 1));
    dispatch(setPageSize(model.pageSize));
  };

  const handleView = async (id: string) => {
    try {
      dispatch(setLoading(true));

      const { data } = await fetchDocument({
        variables: {
          params: {
            ids: [id],
          },
        },
      });

      if (data?.chatbotDocument?.success && data.chatbotDocument.data) {
        const document = data.chatbotDocument.data[0];
        dispatch(setSelectedDocument(document));
        setViewModalOpen(true);
      } else {
        const errorMessage =
          data?.chatbotDocument?.message || 'Error al cargar documento';
        dispatch(setError(errorMessage));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      console.error('Error loading document:', err);
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    dispatch(clearSelectedDocument());
  };

  const handleEdit = async (id: string) => {
    try {
      dispatch(setLoading(true));
      const { data } = await fetchDocument({
        variables: { params: { ids: [id] } },
      });
      if (data?.chatbotDocument?.success && data.chatbotDocument.data) {
        dispatch(setSelectedDocument(data.chatbotDocument.data[0]));
        setSaveError(null);
        setEditModalOpen(true);
      } else {
        dispatch(setError(data?.chatbotDocument?.message || 'Error al cargar documento'));
      }
    } catch (err: any) {
      if (err.name === 'AbortError' || err.message === 'The operation was aborted.') return;
      dispatch(setError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleSaveEdit = async (formData: EditDocumentFormData) => {
    try {
      setSaving(true);
      setSaveError(null);

      const { data: responseData } = await updateDocumentMutation({
        variables: {
          params: {
            id: formData.id,
            description: formData.description,
            tags: formData.tags,
          },
        },
      });

      if (responseData?.updateChatbotDocument?.success) {
        if (responseData.updateChatbotDocument.data) {
          dispatch(updateDocumentAction(responseData.updateChatbotDocument.data));
        }
        setEditModalOpen(false);
        dispatch(clearSelectedDocument());
        setSaveError(null);
        setSuccessMessage('Documento actualizado exitosamente');
      } else {
        const msg = responseData?.updateChatbotDocument?.message || 'Error al actualizar documento';
        const errs = responseData?.updateChatbotDocument?.errors || [];
        setSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    dispatch(clearSelectedDocument());
    setSaveError(null);
    setSaving(false);
  };

  const handleDeleteClick = (document: Document) => {
    setDocumentToDelete(document);
    setConfirmDeleteOpen(true);
    setDeleteError(null);
  };

  const handleDeleteFromModal = () => {
    if (selectedDocument) {
      setViewModalOpen(false);
      setDocumentToDelete(selectedDocument);
      setConfirmDeleteOpen(true);
      setDeleteError(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
    setDocumentToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    try {
      setDeleting(true);
      setDeleteError(null);

      const { data: responseData } = await deleteDocumentMutation({
        variables: {
          params: {
            ids: [documentToDelete.id],
          },
        },
      });

      if (responseData?.deleteChatbotDocument?.success) {
        // Eliminación exitosa
        dispatch(deleteDocumentAction(documentToDelete.id));
        setConfirmDeleteOpen(false);
        setDocumentToDelete(null);
        setSuccessMessage('Documento eliminado exitosamente');
      } else {
        // Error en la eliminación
        const errorMessage =
          responseData?.deleteChatbotDocument?.message || 'Error al eliminar documento';
        const errors = responseData?.deleteChatbotDocument?.errors || [];
        const fullErrorMessage =
          errors.length > 0 ? `${errorMessage}: ${errors.join(', ')}` : errorMessage;

        setDeleteError(fullErrorMessage);
      }
    } catch (err: any) {
      console.error('Error deleting document:', err);
      setDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  const handleOpenUploadModal = () => {
    setUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };

  const handleUploadSuccess = (document: Document) => {
    dispatch(addDocument(document));
    setSuccessMessage('Documento vectorizado exitosamente');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const columns: GridColDef<Document>[] = [
    {
      field: 'fileName',
      headerName: 'Nombre del Archivo',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'fileSize',
      headerName: 'Tamaño',
      width: 120,
      valueGetter: (value, row) => formatFileSize(row.fileSize),
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 2,
      minWidth: 250,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.tags.join(', '),
    },
    {
      field: 'documentType',
      headerName: 'Tipo de Documento',
      width: 180,
      valueGetter: (value, row) => row.documentType.name,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha Creación',
      width: 130,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleView(params.row.id)}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleEdit(params.row.id)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="documents-container">
        <Paper elevation={2} className="documents-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Typography color="text.primary">Documentos Vectorizados</Typography>
          </Breadcrumbs>

          <Box className="documents-header">
            <Typography variant="h4" component="h1">
              Documentos Vectorizados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestión de documentos para búsqueda semántica
            </Typography>
          </Box>

          <Box className="documents-toolbar">
            <Box className="search-box">
              <TextField
                size="small"
                placeholder="Buscar documento..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                sx={{ ml: 1 }}
              >
                Buscar
              </Button>
            </Box>

            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleOpenUploadModal}
              sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
            >
              Vectorizar Documento
            </Button>
          </Box>

          {error && (
            <Box className="error-box">
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Box className="data-grid-container">
            <DataGrid
              rows={documents}
              columns={columns}
              loading={loading || fetchingList}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={{
                page: pagination.page - 1,
                pageSize: pagination.pageSize,
              }}
              onPaginationModelChange={handlePaginationChange}
              paginationMode="server"
              rowCount={pagination.totalRecords}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
          </Box>
        </Paper>

        <ViewDocumentModal
          open={viewModalOpen}
          onClose={handleCloseViewModal}
          onDelete={handleDeleteFromModal}
        />

        <EditDocumentModal
          open={editModalOpen}
          onClose={handleCloseEditModal}
          onSave={handleSaveEdit}
          saving={saving}
          saveError={saveError}
        />

        <UploadDocumentModal
          open={uploadModalOpen}
          onClose={handleCloseUploadModal}
          onSuccess={handleUploadSuccess}
        />

        <ConfirmDeleteDialog
          open={confirmDeleteOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          documentName={documentToDelete?.fileName || ''}
          deleting={deleting}
        />

        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>

        {deleteError && (
          <Snackbar
            open={!!deleteError}
            autoHideDuration={6000}
            onClose={() => setDeleteError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => setDeleteError(null)} severity="error" sx={{ width: '100%' }}>
              {deleteError}
            </Alert>
          </Snackbar>
        )}
      </div>
    </MainLayout>
  );
};

export default Documents;
